/**
 * 后台代登录中转页：消费代登录 token 并建立前台会话。
 */

import type { ReactElement } from 'react'
import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { getCurrentUser } from '@/services/authService'
import { AUTH_TOKEN_KEY } from '@/services/api'
import { useAuthStore } from '@/stores'

/**
 * 后台代登录中转页面
 * @returns ReactElement
 */
export function ImpersonatePage(): ReactElement {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const setSession = useAuthStore((state) => state.setSession)
  const logout = useAuthStore((state) => state.logout)

  useEffect((): (() => void) | undefined => {
    const token = searchParams.get('token')?.trim() ?? ''
    if (token === '') {
      toast.error('缺少代登录令牌，请从后台重新发起')
      void navigate('/login', { replace: true })
      return undefined
    }

    // 读取完成后立即清理地址栏，避免 token 长时间暴露在 URL 中。
    window.history.replaceState(window.history.state, '', '/impersonate')

    let active = true
    const bootstrapSession = async (): Promise<void> => {
      try {
        localStorage.setItem(AUTH_TOKEN_KEY, token)
        const user = await getCurrentUser()
        if (!active) {
          return
        }

        setSession(token, user)
        toast.success(`已登录为 ${user.username}`)
        void navigate('/', { replace: true })
      } catch {
        if (!active) {
          return
        }
        logout()
        toast.error('代登录失败，请稍后重试')
        void navigate('/login', { replace: true })
      }
    }

    void bootstrapSession()

    return (): void => {
      active = false
    }
  }, [navigate, logout, searchParams, setSession])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        正在登录前台账号...
      </div>
    </div>
  )
}
